import * as z from 'zod';
import { 
  LegalFormOHADA, 
  CompanyType, 
  TraditionalSector, 
  StartupSector,
  EnterpriseIdentificationForm 
} from './enterpriseIdentification';

// Types de base pour les adresses
export interface Address {
  street?: string;
  city?: string;
  commune?: string;
  province?: string;
  country?: string;
}

export interface Location {
  id: string;
  name: string;
  type: 'headquarters' | 'branch' | 'store' | 'warehouse' | 'factory' | 'other';
  address?: string;
  coordinates: {
    lat: number;
    lng: number;
  };
}

// Interface pour les associés
export interface Associate {
  id?: string;
  name: string;
  gender?: 'male' | 'female';
  role?: string;
  shares?: number;
  email?: string;
  phone?: string;
}

/**
 * ❌ OBSOLÈTE - Ancienne interface Company (legacy)
 * Cette interface est remplacée par la nouvelle interface Company ci-dessous
 * qui est basée directement sur le formulaire EnterpriseIdentificationForm
 * 
 * @deprecated Utiliser la nouvelle interface Company basée sur CreateCompanyRequest
 */
export interface CompanyLegacy {
  id: string;
  name: string;
  logo?: string;
  description?: string;
  legalForm?: string;
  industry?: string;
  size?: string;
  website?: string;
  facebookPage?: string;

  // Legal & Tax Identifiers
  rccm?: string;
  taxId?: string;
  natId?: string;

  // Address (legacy format)
  address?: Address;
  
  // Locations (new format with coordinates)
  locations?: Location[];

  // Contact Information
  contacts?: {
    email?: string;
    phone?: string;
    altPhone?: string;
  };

  // People
  owner?: {
    id: string;
    name: string;
    gender?: 'male' | 'female';
    email?: string;
    phone?: string;
    hasOtherJob?: boolean;
    cv?: string; // URL du CV sur Cloudinary
    linkedin?: string;
    facebook?: string;
  };
  associates?: Associate[];

  // Business Activities - MIS À JOUR
  activities?: {
    primary?: string;
    secondary?: string[]; // Inclut maintenant les secteurs secondaires + personnalisés
  };

  // Financial Information
  capital?: {
    isApplicable?: boolean;
    amount?: number;
    currency?: 'USD' | 'CDF' | 'EUR';
  };
  financials?: {
    revenue?: number;
    netIncome?: number;
    totalAssets?: number;
    equity?: number;
  };

  // Affiliations
  affiliations?: {
    cnss?: string;
    inpp?: string;
    onem?: string;
    intraCoop?: string;
    interCoop?: string;
    partners?: string[];
  };

  // Subscription details (as seen in the UI)
  subscription?: {
    plan?: {
      name: string;
    };
    status: 'active' | 'inactive' | 'pending' | 'expired';
    currentPeriodEnd?: string;
  };

  // NOUVEAUX CHAMPS - Formulaire d'identification d'entreprise
  // Basé sur EnterpriseIdentificationForm

  // Identification étendue
  identificationEtendue?: {
    raisonSociale?: string;
    sigle?: string;
    formeJuridiqueOHADA?: LegalFormOHADA;
    dateCreation?: string;
    dateDebutActivites?: string;
    dateAutorisationOuverture?: string;
    referenceAutorisationOuverture?: string;
    
    // Incubation et accélération (optionnel)
    enIncubation?: boolean;
    typeAccompagnement?: 'incubation' | 'acceleration';
    nomIncubateurAccelerateur?: string;
    certificatAffiliation?: string; // URL du document PDF
  };

  // Type et secteur d'entreprise
  typeEntreprise?: CompanyType;
  secteurActivite?: TraditionalSector | StartupSector;
  activitePrincipaleDetaille?: {
    description?: string;
    produitsServices?: string;
    marcheCible?: string;
    propositionValeurUnique?: string; // Pour startups
  };
  activiteSecondaire?: {
    secteur?: TraditionalSector | StartupSector;
    description?: string;
  };

  // Spécificités startup
  startupSpecifics?: {
    niveauMaturiteTechnologique?: 'concept_idee' | 'preuve_concept' | 'prototype' | 'demonstrateur' | 'produit_commercial';
    modeleEconomique?: 'b2b' | 'b2c' | 'b2b2c' | 'marketplace_plateforme' | 'freemium' | 'abonnement_saas' | 'commission_pourcentage' | 'publicite' | 'autre';
    modeleEconomiqueAutre?: string;
    proprieteIntellectuelle?: {
      brevetsDeposes?: boolean;
      brevetsDetails?: string;
      marquesDeposees?: boolean;
      marquesDetails?: string;
      droitsAuteur?: boolean;
      droitsAuteurDetails?: string;
    };
  };

  // Spécificités entreprises traditionnelles
  traditionalSpecifics?: {
    certificationQualite?: boolean;
    certificationQualiteDetails?: string;
    licencesExploitation?: boolean;
    licencesExploitationDetails?: string;
  };

  // Capital social étendu
  capitalSocialEtendu?: {
    montant?: number;
    devise?: 'USD' | 'CDF' | 'EUR' | 'MAD';
    repartition?: {
      nom: string;
      sexe: 'male' | 'female';
      nationalite: string;
      valeurApportCapital: number;
      natureApportCapital: string;
      numeroTelephone: string;
      adresse: string;
      natureJuridiqueLocalLDomicile: 'proprietaire' | 'locataire';
      exerceAutresActivites: boolean;
      autresActivitesDetails?: string;
    }[];
  };

  // Immatriculations étendues
  immatriculationsEtendues?: {
    rccmEtendu?: {
      numero: string;
      date: string;
      lieu: string;
    };
    identificationNationale?: {
      numero: string;
      date: string;
      lieu: string;
    };
    impotFiscal?: {
      numero: string;
      date: string;
      lieu: string;
    };
  };

  // Adresses étendues
  adressesEtendues?: {
    siegeSocial?: {
      adresse: string;
      natureJuridiqueLocaux: 'proprietaire' | 'locataire';
    };
    siegeExploitation?: {
      adresse: string;
      natureJuridiqueLocaux: 'proprietaire' | 'locataire';
    };
    contactsEtendus?: {
      telephoneFixe?: string;
      telephoneMobile: string;
      fax?: string;
      boitePostale?: string;
      email: string;
      siteInternet?: string;
    };
  };

  // Références bancaires étendues
  referencesBancairesEtendues?: {
    institution1?: {
      nomBanque: string;
      intituleCompte: string;
      numeroCompte: string;
      devise: 'USD' | 'CDF' | 'EUR' | 'MAD';
    };
    institution2?: {
      nomBanque: string;
      intituleCompte: string;
      numeroCompte: string;
      devise: 'USD' | 'CDF' | 'EUR' | 'MAD';
    };
  };

  // Promoteur du projet
  promoteurProject?: {
    identification: {
      nom: string;
      etatCivil: string;
      qualiteJuridique: string;
      nationalite: string;
      fonctionEntreprise: string;
      nomConjoint?: string;
    };
    contacts: {
      telephoneFixe?: string;
      telephoneMobile: string;
      fax?: string;
      email: string;
      adresseDomicile: string;
      natureJuridiqueLocauxDomicile: 'proprietaire' | 'locataire';
    };
    formationsExperiences: string;
  };

  // Patrimoine et moyens
  patrimoineMoyens?: {
    actifsImmobiliers?: {
      id: string;
      designation: string;
      type: 'immobilier' | 'vehicule' | 'equipement' | 'autre';
      description?: string;
      prixAchat?: number;
      valeurActuelle?: number;
      devise?: 'USD' | 'CDF' | 'EUR';
      dateAcquisition?: string;
      etatActuel?: 'neuf' | 'excellent' | 'bon' | 'moyen' | 'mauvais' | 'deteriore';
      localisation?: string;
      marque?: string;
      modele?: string;
      proprietaire?: 'propre' | 'location' | 'leasing' | 'emprunt';
    }[];
    equipementsMateriels?: {
      id: string;
      designation: string;
      type: 'equipement';
      description?: string;
      prixAchat?: number;
      valeurActuelle?: number;
      devise?: 'USD' | 'CDF' | 'EUR';
      dateAcquisition?: string;
      etatActuel?: 'neuf' | 'excellent' | 'bon' | 'moyen' | 'mauvais' | 'deteriore';
      localisation?: string;
      marque?: string;
      modele?: string;
      capaciteProduction?: string;
    }[];
    equipementsTechnologiques?: {
      type: 'serveurs' | 'materiel_dev' | 'equipement_labo' | 'prototypes' | 'licences_logicielles';
      description: string;
      anneeAcquisition: string;
      valeur: number;
    }[];
    stocks?: {
      id: string;
      designation: string;
      categorie: 'matiere_premiere' | 'produit_semi_fini' | 'produit_fini' | 'fourniture' | 'emballage' | 'autre';
      description?: string;
      quantiteStock: number;
      unite: string;
      coutUnitaire: number;
      valeurTotaleStock: number;
      devise: 'USD' | 'CDF' | 'EUR';
      dateDernierInventaire?: string;
      dureeRotationMoyenne?: number;
      etatStock: 'excellent' | 'bon' | 'moyen' | 'deteriore' | 'perime';
      emplacement?: string;
      fournisseurPrincipal?: string;
    }[];
  };

  // Performance et métriques
  performanceMetriques?: {
    statistiquesPerformance?: {
      annee: string;
      volumeProduction?: number;
      chiffreAffaires: number;
      principalesDifficultees: string;
      // Pour startups
      utilisateursClients?: number;
      revenus?: number;
      financementLeve?: number;
      jalonsTechniques?: string;
    }[];
    metriquesStartup?: {
      mrr?: number; // Monthly Recurring Revenue
      churnRate?: number;
      cac?: number; // Customer Acquisition Cost
      gmv?: number; // Gross Merchandise Value
      nombreTransactions?: number;
      panierMoyen?: number;
      telechargementsApp?: number;
      utilisateursActifs?: number;
      retention?: number;
    };
  };

  // Structure organisationnelle étendue
  structureOrganisationnelleEtendue?: {
    personnelCle?: {
      nom: string;
      sexe: 'male' | 'female';
      posteOccupe: string;
      tachesPrecises: string;
      dateEngagement: string;
      formationDiplomes: string;
      experienceAnterieure: string;
      competencesTechniques?: string[];
      competencesBusiness?: string[];
    }[];
    structureGouvernance?: {
      // Entreprises traditionnelles
      organigrammeHierarchique?: string;
      repartitionResponsabilites?: string;
      processusPriseDecision?: string;
      // Startups
      structureAgile?: string;
      advisoryBoard?: string;
      investisseursGouvernance?: string;
      methodesTravaif?: string;
    };
    sousTraitancePartenariats?: {
      sousTraitanceClassique: {
        activitesSousTraitees: boolean;
        detailsActivites?: string;
      };
      partenariatsStartup?: {
        partenairesTechniques: string;
        prestatairesExternes: string;
        partenairesDistribution: string;
        fournisseursServices: string;
      };
    };
  };

  // Renseignements financiers et juridiques étendus
  financierJuridiqueEtendu?: {
    financementInvestissements?: {
      concoursFinanciersTraditionnels: {
        aBeneficieDeprets: boolean;
        prets?: {
          objetPret: string;
          montantTotal: number;
          encours: number;
          conditionsRemboursement: string;
        }[];
      };
      leveesfondsStartup?: {
        aLeveFonds: boolean;
        toursFinancement?: {
          typeTour: string;
          montantLeve: number;
          valorisationPreMoney: number;
          valorisationPostMoney: number;
          investisseurs: string;
          date: string;
          utilisationFonds: string;
        }[];
      };
    };
    questionsJuridiques?: {
      proprieteIntellectuelle: {
        brevetsDeposes: boolean;
        marquesProtegees: boolean;
        accordsConfidentialite: boolean;
        litigesPI: boolean;
      };
      conformiteReglementaire: {
        conformiteRGPD: 'oui' | 'non' | 'en_cours';
        licencesSectorielles: boolean;
        certificationsQualite: boolean;
      };
      antecedentsJuridiques: {
        failliteInsolvabilite: boolean;
        detailsFaillite?: string;
        poursuitesjudiciaires: boolean;
        detailsPoursuites?: string;
        garantPrets: boolean;
        detailsGarants?: string;
        antecedensFiscaux: boolean;
        detailsFiscaux?: string;
      };
    };
  };

  // Formulaire d'identification complet (référence)
  formulaireIdentificationComplet?: EnterpriseIdentificationForm;

  // Statut du formulaire d'identification
  formulaireStatut?: {
    estComplete: boolean;
    derniereMiseAJour: string;
    etapesCompletees: string[];
    prochainEtapes: string[];
    documentsJoints: string[];
    scoreCompletion: number;
    // Nouveaux champs pour la soumission
    statutValidation?: 'brouillon' | 'soumis' | 'en_validation' | 'valide' | 'rejete';
    datesoumission?: string;
    dateValidation?: string;
    commentairesValidation?: string;
  };

  // Métadonnées
  createdAt: string;
  updatedAt?: string;
}

// Interface pour les données API (version backend/API)
/**
 * CompanyResponse - Ce que le backend renvoie
 * ✨ SIMPLIFIÉ: Le backend renvoie exactement ce qu'il reçoit + métadonnées
 * Pas de transformation, pas de perte de données
 */
export interface CompanyResponse extends Company {
  id: string;
  createdAt: string;
  updatedAt: string;
}

// Types pour les requêtes d'API
// Interface pour les coordonnées géographiques
export interface Coordinates {
  latitude: number;
  longitude: number;
  address?: string;
}

// Interface pour les réseaux sociaux
export interface SocialLink {
  platform: string;
  url: string;
  label: string;
}

// Interface pour le pitch
export interface PitchData {
  elevator_pitch?: string;
  value_proposition?: string;
  target_market?: string;
  competitive_advantage?: string;
  pitch_deck_url?: string;
  demo_video_url?: string;
}

// Interface pour une personne (dirigeant, employé, actionnaire)
export interface PersonData {
  id: string;
  nom: string;
  prenoms: string;
  fonction: string;
  nationalite?: string;
  telephone?: string;
  email?: string;
  adresse?: string;
  pourcentageActions?: number;
  dateNomination?: string;
  typeContrat?: string;
  salaire?: number;
  diplomes?: string[];
}

// Interface pour un actif (immobilisation, équipement, véhicule)
export interface AssetData {
  id: string;
  designation: string;
  type: 'immobilier' | 'vehicule' | 'equipement' | 'stock' | 'autre';
  description?: string;
  prixAchat?: number;
  valeurActuelle?: number;
  devise?: 'USD' | 'CDF' | 'EUR';
  dateAcquisition?: string;
  etatActuel?: 'neuf' | 'excellent' | 'bon' | 'moyen' | 'mauvais' | 'deteriore';
  localisation?: string;
  numeroSerie?: string;
  marque?: string;
  modele?: string;
  quantite?: number;
  unite?: string;
  proprietaire?: 'propre' | 'location' | 'leasing' | 'emprunt';
  observations?: string;
}

// Interface pour un stock
export interface StockData {
  id: string;
  designation: string;
  categorie: 'matiere_premiere' | 'produit_semi_fini' | 'produit_fini' | 'fourniture' | 'emballage' | 'autre';
  description?: string;
  quantiteStock: number;
  unite: string;
  seuilMinimum?: number;
  seuilMaximum?: number;
  coutUnitaire: number;
  valeurTotaleStock: number;
  devise: 'USD' | 'CDF' | 'EUR';
  dateDernierInventaire?: string;
  dureeRotationMoyenne?: number;
  datePeremption?: string;
  emplacement?: string;
  conditionsStockage?: string;
  fournisseurPrincipal?: string;
  numeroLot?: string;
  codeArticle?: string;
  etatStock: 'excellent' | 'bon' | 'moyen' | 'deteriore' | 'perime';
  observations?: string;
}

// Interface pour un document uploadé
export interface DocumentFile {
  url: string;
  name: string;
  type?: string;
  size?: number;
  uploadedAt?: string;
}

/**
 * Interface Company - BASÉE SUR LE FORMULAIRE EnterpriseIdentificationForm
 * SOURCE DE VÉRITÉ: src/components/company/EnterpriseIdentificationForm.tsx
 * 
 * Cette interface représente TOUTES les données du formulaire d'identification d'entreprise
 * Aucune transformation, pas de perte de données
 */
export interface Company {
  // ============================================
  // MÉTADONNÉES (ajoutées par le backend)
  // ============================================
  id?: string;
  createdAt?: string;
  updatedAt?: string;
  
  // ============================================
  // ASSETS ET MÉDIAS
  // ============================================
  logo?: string; // URL du logo (Cloudinary ou autre)
  
  // ============================================
  // SECTION: Identification de Base
  // ============================================
  raisonSociale: string;
  sigle?: string;
  formeJuridiqueOHADA?: string;
  dateCreation?: string;
  dateDebutActivites?: string;
  numeroRCCM?: string;
  numeroIdentificationNationale?: string;
  numeroImpotFiscal?: string;
  
  // ============================================
  // SECTION: Nature des Activités
  // ============================================
  typeEntreprise: 'traditional' | 'startup';
  secteurActivitePrincipal: string;
  secteursActiviteSecondaires?: string[];
  secteursPersonalises?: string[]; // Secteurs ajoutés manuellement
  descriptionActivites?: string;
  produitsServices?: string[];
  
  // ============================================
  // SECTION: Informations Complémentaires
  // ============================================
  tailleEntreprise?: string;
  capitalSocial?: string;
  deviseCapital?: 'USD' | 'CDF' | 'EUR';
  
  // ============================================
  // SECTION: Incubation et Accélération
  // ============================================
  enIncubation?: boolean;
  typeAccompagnement?: 'incubation' | 'acceleration';
  nomIncubateurAccelerateur?: string;
  certificatAffiliation?: DocumentFile[]; // Documents PDF
  
  // ============================================
  // SECTION: Coordonnées et Localisation
  // ============================================
  siegeSocial?: Coordinates;
  siegeExploitation?: Coordinates;
  unitesProduction?: Coordinates[];
  pointsVente?: Coordinates[];
  
  // ============================================
  // SECTION: Contacts et Réseaux Sociaux
  // ============================================
  telephoneFixe?: string;
  telephoneMobile?: string;
  fax?: string;
  email?: string;
  boitePostale?: string;
  reseauxSociaux?: SocialLink[];
  
  // ============================================
  // SECTION: Pitch et Présentation
  // ============================================
  pitch?: PitchData;
  
  // ============================================
  // SECTION: Spécificités Startup
  // ============================================
  niveauMaturiteTechnologique?: string;
  modeleEconomique?: string;
  proprieteIntellectuelle?: string[];
  
  // ============================================
  // SECTION: Spécificités Traditionnelles
  // ============================================
  certificationQualite?: boolean;
  licencesExploitation?: string[];
  
  // ============================================
  // SECTION: Structure Organisationnelle
  // ============================================
  dirigeants?: PersonData[];
  employes?: PersonData[];
  actionnaires?: PersonData[];
  nombreEmployes?: number;
  organigramme?: string;
  
  // ============================================
  // SECTION: Patrimoine et Moyens
  // ============================================
  immobilisations?: AssetData[];
  equipements?: AssetData[];
  vehicules?: AssetData[];
  stocks?: StockData[];
  moyensTechniques?: string[];
  capaciteProduction?: string;
  
  // ============================================
  // SECTION: Financier et Juridique
  // ============================================
  comptesBancaires?: any[];
  assurances?: any[];
  financements?: any[];
  litigesEnCours?: boolean;
  detailsLitiges?: string;
  failliteAnterieure?: boolean;
  detailsFaillite?: string;
  poursuiteJudiciaire?: boolean;
  detailsPoursuites?: string;
  garantiePrets?: boolean;
  detailsGaranties?: string;
  antecedentesFiscales?: boolean;
  detailsAntecedentes?: string;
  
  // ============================================
  // SECTION: Documents Obligatoires
  // ============================================
  documentsEntreprise?: DocumentFile[];
  documentsPersonnel?: DocumentFile[];
  documentsFinanciers?: DocumentFile[];
  documentsPatrimoine?: DocumentFile[];
  documentsProprietéIntellectuelle?: DocumentFile[];
  documentsSectoriels?: DocumentFile[];
}

/**
 * Request pour mettre à jour une entreprise
 * Tous les champs sont optionnels (l'ID est passé dans l'URL)
 */
export interface UpdateCompanyRequest extends Partial<Company> {
  // Pas besoin d'ID ici, il est passé dans l'URL
}

/**
 * Alias pour compatibilité avec l'ancien code
 * @deprecated Utiliser Company directement
 */
export type CreateCompanyRequest = Company;

// Types pour les filtres
export interface CompanyFilters {
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  industry?: string;
  size?: string;
  province?: string;
  legalForm?: string;
  search?: string;
}

// Schéma de validation Zod pour Company
export const companySchema = z.object({
  id: z.string().optional(),
  name: z.string().min(2, "Le nom de l'entreprise doit contenir au moins 2 caractères."),
  logo: z.string().url().optional().or(z.literal('')),
  description: z.string().optional(),
  legalForm: z.string().optional(),
  industry: z.string().optional(),
  size: z.string().optional(),
  website: z.string().url("L'URL du site web est invalide.").optional().or(z.literal('')),
  facebookPage: z.string().url("L'URL de la page Facebook est invalide.").regex(/facebook\.com/, "L'URL doit être une page Facebook valide.").optional().or(z.literal('')),
  
  // Legal & Tax Identifiers
  rccm: z.string().optional(),
  taxId: z.string().optional(),
  natId: z.string().optional(),
  
  // Address
  address: z.object({
    street: z.string().optional(),
    city: z.string().optional(),
    commune: z.string().optional(),
    province: z.string().optional(),
    country: z.string().optional(),
  }).optional(),
  
  // Locations
  locations: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.enum(['headquarters', 'branch', 'store', 'warehouse', 'factory', 'other']),
      address: z.string().optional(),
      coordinates: z.object({
        lat: z.number(),
        lng: z.number(),
      }),
    })
  ).optional(),
  
  // Contacts
  contacts: z.object({
    email: z.string().email("L'adresse email de l'entreprise est requise."),
    phone: z.string().min(1, "Le téléphone de contact est requis."),
    altPhone: z.string().optional(),
  }).optional(),
  
  // Owner
  owner: z.object({
    id: z.string().optional(),
    name: z.string().min(1, "Le nom du dirigeant est requis."),
    gender: z.enum(['male', 'female']).optional(),
    email: z.string().email("L'email du dirigeant est requis."),
    phone: z.string().optional(),
    hasOtherJob: z.boolean().optional(),
    cv: z.string().url().optional().or(z.literal('')),
    linkedin: z.string().url("L'URL LinkedIn est invalide.").regex(/linkedin\.com\/in\//, "L'URL LinkedIn doit être au format linkedin.com/in/...").optional().or(z.literal('')),
    facebook: z.string().url("L'URL Facebook est invalide.").regex(/facebook\.com/, "L'URL doit être un profil Facebook valide.").optional().or(z.literal('')),
  }).optional(),
  
  // Associates
  associates: z.array(
    z.object({
      id: z.string().optional(),
      name: z.string(),
      gender: z.enum(['male', 'female']).optional(),
      role: z.string().optional(),
      shares: z.number().min(0).max(100).optional(),
      email: z.string().email("L'email de l'associé est invalide.").optional().or(z.literal('')),
      phone: z.string().optional(),
    })
  ).optional(),
  
  // Activities - MIS À JOUR
  activities: z.object({
    primary: z.string().optional(),
    secondary: z.array(z.string()).optional(),
  }).optional(),
  
  // Nouveau: Secteurs personnalisés
  secteursPersnnalises: z.array(z.string()).optional(),
  
  // Capital
  capital: z.object({
    isApplicable: z.boolean().optional(),
    amount: z.number().optional(),
    currency: z.enum(['USD', 'CDF', 'EUR']).optional(),
  }).optional(),
  
  // Affiliations
  affiliations: z.object({
    cnss: z.string().optional(),
    inpp: z.string().optional(),
    onem: z.string().optional(),
    intraCoop: z.string().optional(),
    interCoop: z.string().optional(),
    partners: z.array(z.string()).optional(),
  }).optional(),
});

// Type inféré du schéma Zod
export type CompanyFormData = z.infer<typeof companySchema>;

// Fonctions utilitaires pour la conversion entre les types
/**
 * ✨ PAS DE CONVERSION NÉCESSAIRE !
 * Le backend renvoie CompanyResponse = Company + métadonnées
 * Donc CompanyResponse contient déjà TOUTES les données du formulaire
 * On peut l'utiliser directement sans transformation
 */

/**
 * Helpers pour gérer le propriétaire (owner)
 * Le propriétaire est le premier dirigeant de la liste
 */
export const CompanyHelpers = {
  /**
   * Récupère le propriétaire (dirigeant principal) de l'entreprise
   */
  getOwner(company: Company): PersonData | undefined {
    return company.dirigeants?.[0];
  },

  /**
   * Définit le propriétaire (dirigeant principal) de l'entreprise
   */
  setOwner(company: Company, owner: PersonData): Company {
    return {
      ...company,
      dirigeants: [owner, ...(company.dirigeants?.slice(1) || [])]
    };
  },

  /**
   * Met à jour le CV du propriétaire
   */
  updateOwnerCV(company: Company, cvUrl: string): Company {
    const owner = CompanyHelpers.getOwner(company);
    if (!owner) {
      // Créer un propriétaire par défaut
      return CompanyHelpers.setOwner(company, {
        id: 'owner-1',
        nom: '',
        prenoms: '',
        fonction: 'Dirigeant Principal',
        diplomes: [cvUrl], // Stocker le CV dans diplomes pour l'instant
      });
    }
    
    // Mettre à jour le CV du propriétaire existant
    const updatedOwner = {
      ...owner,
      diplomes: [...(owner.diplomes || []), cvUrl]
    };
    
    return CompanyHelpers.setOwner(company, updatedOwner);
  }
};